import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { Search, Plus, Trash2, Loader2, Link2, X } from 'lucide-react';

/**
 * MappingsManagerDialog
 *
 * Two modes:
 *   mode="control"        Manage all mappings for a single master control.
 *                         Props: masterControl {control_id, name}
 *   mode="requirement"    Manage all mappings for a single requirement.
 *                         Props: requirement {id, external_code, framework_id, title}
 *
 * The dialog lists current mappings on the left and provides an add-picker
 * on the right (framework-aware in control mode, control-aware in
 * requirement mode).
 */
export default function MappingsManagerDialog({
  open, onOpenChange,
  mode,           // 'control' | 'requirement'
  masterControl,  // used when mode === 'control'
  requirement,    // used when mode === 'requirement'
  onChanged,
}) {
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState([]);       // existing mappings
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  // Framework picker (control mode only)
  const [frameworks, setFrameworks] = useState([]);
  const [pickerFramework, setPickerFramework] = useState('');
  const [pickerOptions, setPickerOptions] = useState([]);

  // Control picker (requirement mode)
  const [controlOptions, setControlOptions] = useState([]);

  // -----------------------------------------------------------------
  // Load current mappings + picker options
  // -----------------------------------------------------------------
  const reload = async () => {
    if (!open) return;
    setLoading(true);
    try {
      if (mode === 'control') {
        const [mapRes, fwRes] = await Promise.all([
          axios.get(`${API}/master-controls/${masterControl.control_id}/mappings`),
          axios.get(`${API}/frameworks-v2`),
        ]);
        // Flatten by_framework -> list of mappings with framework meta
        const flat = [];
        mapRes.data.by_framework.forEach((f) => {
          f.mappings.forEach((m) => flat.push({ ...m, framework: f.framework }));
        });
        setCurrent(flat);
        setFrameworks(fwRes.data);
        if (!pickerFramework && fwRes.data.length) setPickerFramework(fwRes.data[0].id);
      } else {
        // requirement mode: get req detail (already has mapped_controls) + all master controls list
        const [detailRes, allRes] = await Promise.all([
          axios.get(`${API}/frameworks-v2/${encodeURIComponent(requirement.framework_id)}/requirements/${requirement.id}`),
          axios.get(`${API}/master-controls?limit=2000`),
        ]);
        setCurrent(detailRes.data.mapped_controls.map((c) => ({
          id: c.mapping_id,
          master_control_id: c.control_id,
          control_name: c.name,
          control_description: c.description,
          rationale: c.rationale,
        })));
        setControlOptions(allRes.data.items || []);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load mappings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) reload(); }, [open]);

  // -----------------------------------------------------------------
  // Framework requirements for picker (control mode)
  // -----------------------------------------------------------------
  useEffect(() => {
    if (mode !== 'control' || !open || !pickerFramework) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/frameworks-v2/${encodeURIComponent(pickerFramework)}/requirements`);
        setPickerOptions(res.data.requirements || []);
      } catch {
        // silent
      }
    })();
  }, [mode, open, pickerFramework]);

  // -----------------------------------------------------------------
  // Filter helpers
  // -----------------------------------------------------------------
  const currentIdSet = useMemo(() => {
    if (mode === 'control') return new Set(current.map((m) => m.requirement_id));
    return new Set(current.map((m) => m.master_control_id));
  }, [mode, current]);

  const filteredPicker = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (mode === 'control') {
      return pickerOptions
        .filter((r) => !currentIdSet.has(r.id))
        .filter((r) => !s || r.external_code.toLowerCase().includes(s) || (r.title || '').toLowerCase().includes(s) || (r.category || '').toLowerCase().includes(s))
        .slice(0, 200);
    }
    return controlOptions
      .filter((c) => !currentIdSet.has(c.control_id))
      .filter((c) => !s || c.control_id.toLowerCase().includes(s) || (c.name || '').toLowerCase().includes(s) || (c.domain || '').toLowerCase().includes(s))
      .slice(0, 200);
  }, [mode, pickerOptions, controlOptions, currentIdSet, search]);

  // -----------------------------------------------------------------
  // Add / remove
  // -----------------------------------------------------------------
  const addMapping = async (pickerItem) => {
    setSaving(true);
    try {
      const body = mode === 'control'
        ? { master_control_id: masterControl.control_id, requirement_id: pickerItem.id }
        : { master_control_id: pickerItem.control_id, requirement_id: requirement.id };
      await axios.post(`${API}/framework-mappings`, body);
      toast.success('Mapping added');
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add mapping');
    } finally {
      setSaving(false);
    }
  };

  const removeMapping = async (mappingId) => {
    if (!window.confirm('Remove this mapping?')) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/framework-mappings/${mappingId}`);
      toast.success('Mapping removed');
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove mapping');
    } finally {
      setSaving(false);
    }
  };

  const editRationale = async (mappingId, newRat) => {
    try {
      await axios.patch(`${API}/framework-mappings/${mappingId}`, { rationale: newRat || null });
      toast.success('Rationale saved');
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save rationale');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="mappings-manager-dialog"
        className="max-w-5xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-slate-600" />
            Manage mappings
          </DialogTitle>
          <DialogDescription>
            {mode === 'control'
              ? <>Master control <span className="font-mono">{masterControl?.control_id}</span> — {masterControl?.name}</>
              : <>Requirement <span className="font-mono">{requirement?.external_code}</span> ({requirement?.framework_id})</>}
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-4 mt-3">
          {/* Left: current mappings */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-700">
              Current mappings ({current.length})
            </div>
            <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100">
              {loading ? (
                <div className="p-6 text-center text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                </div>
              ) : current.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-400 italic">No mappings yet.</div>
              ) : (
                current.map((m) => (
                  <div key={m.id} className="px-3 py-2" data-testid={`mapping-item-${m.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        {mode === 'control' ? (
                          <>
                            <div className="flex items-center gap-2">
                              <Badge
                                variant="outline"
                                style={{ borderColor: m.framework?.color, color: m.framework?.color }}
                              >
                                {m.framework?.short_name}
                              </Badge>
                              <span className="font-mono text-sm text-slate-800">{m.external_code}</span>
                            </div>
                            {m.requirement?.title && (
                              <div className="text-xs text-slate-600 mt-0.5">{m.requirement.title}</div>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="font-mono text-sm text-slate-800">{m.master_control_id}</div>
                            <div className="text-xs text-slate-700 line-clamp-1">{m.control_name}</div>
                            <div className="text-xs text-slate-500 line-clamp-1">{m.control_description}</div>
                          </>
                        )}
                        <Textarea
                          placeholder="Add rationale…"
                          defaultValue={m.rationale || ''}
                          rows={1}
                          className="mt-2 text-xs"
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if ((v || null) !== (m.rationale || null)) editRationale(m.id, v);
                          }}
                          data-testid={`mapping-rationale-${m.id}`}
                        />
                      </div>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => removeMapping(m.id)}
                        disabled={saving}
                        data-testid={`mapping-remove-${m.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right: add-picker */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-700 flex items-center gap-2">
              Add mapping
            </div>
            <div className="p-3 space-y-2 border-b border-slate-100 bg-white">
              {mode === 'control' && (
                <Select value={pickerFramework} onValueChange={setPickerFramework}>
                  <SelectTrigger data-testid="picker-framework"><SelectValue placeholder="Framework" /></SelectTrigger>
                  <SelectContent>
                    {frameworks.map((f) => <SelectItem key={f.id} value={f.id}>{f.short_name || f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={mode === 'control' ? 'Search requirements…' : 'Search master controls…'}
                  className="pl-9"
                  data-testid="picker-search"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="max-h-[360px] overflow-y-auto divide-y divide-slate-100">
              {filteredPicker.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-400 italic">
                  Nothing to add. Refine your search or select another framework.
                </div>
              ) : (
                filteredPicker.map((it) => (
                  <div
                    key={mode === 'control' ? it.id : it.control_id}
                    className="px-3 py-2 flex items-center justify-between hover:bg-slate-50"
                  >
                    <div className="min-w-0 flex-1">
                      {mode === 'control' ? (
                        <>
                          <div className="font-mono text-sm text-slate-800">{it.external_code}</div>
                          <div className="text-xs text-slate-500 truncate">
                            {it.title || <em className="text-slate-400">Official title not set</em>}
                          </div>
                          {it.category && (
                            <Badge variant="outline" className="mt-1 text-[10px]">{it.category}</Badge>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="font-mono text-sm text-slate-800">{it.control_id}</div>
                          <div className="text-xs text-slate-700 truncate">{it.name}</div>
                          <div className="text-[11px] text-slate-500 truncate">{it.domain}</div>
                        </>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => addMapping(it)}
                      disabled={saving}
                      data-testid={`picker-add-${mode === 'control' ? it.external_code : it.control_id}`}
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <Button onClick={() => onOpenChange(false)} data-testid="mappings-manager-close">Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
